import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useGameStore, AVATAR_UNLOCK_CONDITIONS, DEFAULT_AVATAR } from '../store/gameStore';
import { gameCategories, type GameConfig } from '../data/games';
import HomeDashboard from '../components/home/HomeDashboard';
import '../styles/home-terminal.css';
import { BarChart3, X, Moon, Sun, Eye, Clock, Sunset, Download } from 'lucide-react';

// Pre-generated cartoon avatar images
const PRESET_IMAGE_AVATARS = [
  { id: 'archer-girl', src: '/avatars/archer-girl.png', label: '飞羽射手' },
  { id: 'golden-warrior', src: '/avatars/golden-warrior.png', label: '金甲将军' },
  { id: 'swordsman-blue', src: '/avatars/swordsman-blue.png', label: '少年剑客' },
  { id: 'monk-orange', src: '/avatars/monk-orange.png', label: '小武僧' },
  { id: 'scholar-sword', src: '/avatars/scholar-sword.png', label: '书生侠客' },
  { id: 'forest-archer', src: '/avatars/forest-archer.png', label: '林中猎手' },
  { id: 'winged-general', src: '/avatars/winged-general.png', label: '天翼战神' },
  { id: 'zen-monk', src: '/avatars/zen-monk.png', label: '禅武小僧' },
  { id: 'wizard-boy', src: '/avatars/wizard-boy.png', label: '小魔法师' },
  { id: 'sunflower-fairy', src: '/avatars/sunflower-fairy.png', label: '向日葵精灵' },
  { id: 'firefighter-kid', src: '/avatars/firefighter-kid.png', label: '小消防员' },
  { id: 'dj-cool', src: '/avatars/dj-cool.png', label: '酷炫DJ' },
  { id: 'dino-boy', src: '/avatars/dino-boy.png', label: '恐龙宝宝' },
  { id: 'cat-girl', src: '/avatars/cat-girl.png', label: '猫咪女孩' },
  { id: 'robot-kid', src: '/avatars/robot-kid.png', label: '机器人' },
  { id: 'mermaid-girl', src: '/avatars/mermaid-girl.png', label: '美人鱼' },
  { id: 'pirate-captain', src: '/avatars/pirate-captain.png', label: '海盗船长' },
  { id: 'fairy-princess', src: '/avatars/fairy-princess.png', label: '仙女公主' },
  { id: 'ninja-kid', src: '/avatars/ninja-kid.png', label: '小忍者' },
  { id: 'astronaut-boy', src: '/avatars/astronaut-boy.png', label: '宇航员' },
  { id: 'star-captain', src: '/avatars/star-captain.png', label: '星际船长' },
  { id: 'iron-knight', src: '/avatars/iron-knight.png', label: '铁甲骑士' },
  { id: 'lightning-kid', src: '/avatars/lightning-kid.png', label: '闪电小子' },
  { id: 'fox-fairy', src: '/avatars/fox-fairy.png', label: '灵狐仙子' },
  { id: 'sports-star', src: '/avatars/sports-star.png', label: '运动健将' },
  { id: 'snow-queen', src: '/avatars/snow-queen.png', label: '冰雪女王' },
  { id: 'rainbow-painter', src: '/avatars/rainbow-painter.png', label: '彩虹小画家' },
  { id: 'panda-chef', src: '/avatars/panda-chef.png', label: '熊猫大厨' },
  { id: 'dragon-rider', src: '/avatars/dragon-rider.png', label: '驭龙少年' },
  { id: 'moon-fairy', src: '/avatars/moon-fairy.png', label: '月光仙子' },
  { id: 'rock-star', src: '/avatars/rock-star.png', label: '摇滚明星' },
  { id: 'little-witch', src: '/avatars/little-witch.png', label: '小魔女' },
];

const isImageAvatar = (avatar?: string) =>
  Boolean(avatar) && (avatar!.startsWith('data:image') || avatar!.startsWith('http') || avatar!.startsWith('/avatars/'));

export default function HomePage() {
  const navigate = useNavigate();
  const {
    energy,
    badges,
    completedLevels,
    playerName,
    playerAvatar,
    setPlayerName,
    setPlayerAvatar,
    totalEnergy,
    darkMode,
    toggleDarkMode,
    eyeProtection,
    toggleEyeProtection,
    colorTemperature,
    setColorTemperature,
    eyeReminderEnabled,
    toggleEyeReminder,
    eyeReminderInterval,
    setEyeReminderInterval,
    eyeProtectionTodaySeconds,
    eyeProtectionTotalSeconds,
    eyeProtectionWeekly,
    eyeProtectionStartTime,
    autoNightEyeProtection,
    toggleAutoNightEyeProtection,
    nightStartHour,
    nightEndHour,
    setNightStartHour,
    setNightEndHour,
    checkinStreak,
  } = useGameStore();
  const unlockedAvatars = useGameStore(s => s.unlockedAvatars);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNameInput, setShowNameInput] = useState(!playerName);
  const [nameInput, setNameInput] = useState('');
  const [draftAvatar, setDraftAvatar] = useState(
    isImageAvatar(playerAvatar) ? playerAvatar : DEFAULT_AVATAR
  );
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showEyePanel, setShowEyePanel] = useState(false);
  const [liveElapsed, setLiveElapsed] = useState(0);

  useEffect(() => {
    setDraftAvatar(isImageAvatar(playerAvatar) ? playerAvatar : DEFAULT_AVATAR);
  }, [playerAvatar]);

  // Live timer for current session
  useEffect(() => {
    if (!eyeProtection || !eyeProtectionStartTime) {
      setLiveElapsed(0);
      return;
    }
    const update = () => {
      setLiveElapsed(Math.floor((Date.now() - eyeProtectionStartTime) / 1000));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [eyeProtection, eyeProtectionStartTime]);

  // Helper: format seconds to readable duration
  const formatDuration = useCallback((totalSeconds: number) => {
    if (totalSeconds < 60) return `${totalSeconds}秒`;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) return `${hours}时${minutes}分`;
    return `${minutes}分钟`;
  }, []);

  // Helper: get last 7 days labels
  const getLast7Days = useCallback(() => {
    const days = [];
    const weekDayNames = ['日', '一', '二', '三', '四', '五', '六'];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      days.push({
        date: d.toISOString().split('T')[0],
        label: weekDayNames[d.getDay()],
      });
    }
    return days;
  }, []);

  // Export eye protection report
  const exportEyeReport = useCallback(() => {
    const today = new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
    const totalSecs = eyeProtectionTotalSeconds + liveElapsed;
    const todaySecs = eyeProtectionTodaySeconds + liveElapsed;
    const weeklyRows = getLast7Days().map(day => {
      const record = eyeProtectionWeekly.find(r => r.date === day.date);
      const secs = record?.seconds || 0;
      return `<tr><td style="padding:8px 12px;border-bottom:1px solid #f3e8d0;">${day.date} (周${day.label})</td><td style="padding:8px 12px;border-bottom:1px solid #f3e8d0;text-align:right;">${formatDuration(secs)}</td></tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="zh-CN">
<head><meta charset="UTF-8"><title>护眼使用报告 - ${playerName || '探险者'}</title>
<style>
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:600px;margin:40px auto;padding:20px;color:#44403c;background:#fffbeb;}
h1{color:#b45309;margin-bottom:4px;}
.subtitle{color:#92400e;font-size:14px;margin-bottom:24px;}
.card{background:#fff;border-radius:12px;padding:20px;margin-bottom:16px;box-shadow:0 2px 8px rgba(0,0,0,0.05);}
.stats-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;}
.stat-item{background:#fef3c7;border-radius:8px;padding:12px;text-align:center;}
.stat-value{font-size:20px;font-weight:bold;color:#b45309;}
.stat-label{font-size:12px;color:#92400e;margin-top:4px;}
table{width:100%;border-collapse:collapse;font-size:14px;}
th{background:#fef3c7;padding:8px 12px;text-align:left;color:#92400e;}
.footer{text-align:center;font-size:11px;color:#a8a29e;margin-top:24px;}
.settings{font-size:13px;line-height:1.8;color:#57534e;}
</style></head>
<body>
<h1>👁️ 护眼使用报告</h1>
<p class="subtitle">${playerName || '探险者'} · ${today}</p>

<div class="card">
<h3 style="margin-top:0;color:#b45309;">📊 使用时长</h3>
<div class="stats-grid">
<div class="stat-item"><div class="stat-value">${formatDuration(todaySecs)}</div><div class="stat-label">今日使用</div></div>
<div class="stat-item"><div class="stat-value">${formatDuration(totalSecs)}</div><div class="stat-label">累计使用</div></div>
</div>
</div>

<div class="card">
<h3 style="margin-top:0;color:#b45309;">📅 近7日使用详情</h3>
<table><thead><tr><th>日期</th><th style="text-align:right;">时长</th></tr></thead><tbody>${weeklyRows}</tbody></table>
</div>

<div class="card">
<h3 style="margin-top:0;color:#b45309;">⚙️ 当前设置</h3>
<div class="settings">
<p>🎨 色温强度：${colorTemperature}%</p>
<p>⏰ 定时提醒：${eyeReminderEnabled ? `每${eyeReminderInterval}分钟` : '已关闭'}</p>
<p>🌙 自动夜间护眼：${autoNightEyeProtection ? `${String(nightStartHour).padStart(2,'0')}:00 - ${String(nightEndHour).padStart(2,'0')}:00` : '已关闭'}</p>
</div>
</div>

<p class="footer">由「视界探险」护眼模式生成 · ${today}</p>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `护眼报告_${playerName || '探险者'}_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [eyeProtectionTotalSeconds, eyeProtectionTodaySeconds, eyeProtectionWeekly, liveElapsed, playerName, colorTemperature, eyeReminderEnabled, eyeReminderInterval, autoNightEyeProtection, nightStartHour, nightEndHour, formatDuration, getLast7Days]);

  const closeAvatarPicker = useCallback(() => setShowAvatarPicker(false), []);

  const openAvatarPicker = useCallback(() => setShowAvatarPicker(true), []);

  const handleNameSubmit = () => {
    if (nameInput.trim()) {
      setPlayerName(nameInput.trim());
      setPlayerAvatar(draftAvatar || DEFAULT_AVATAR);
      setShowNameInput(false);
    }
  };

  const handlePresetImageAvatarSelect = (src: string) => {
    setPlayerAvatar(src);
    if (playerName) closeAvatarPicker();
  };

  const filteredGames = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    return gameCategories.flatMap(cat => cat.games).filter(g =>
      g.name.toLowerCase().includes(q) || g.description.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const handleGameClick = (game: GameConfig) => {
    navigate(`/game/${game.id}`, { state: { gameConfig: game } });
  };

  const totalGames = gameCategories.reduce((sum, cat) => sum + cat.games.length, 0);

  return (
    <div className="cyber-home">
      <div className="cyber-grid" aria-hidden="true" />

      {/* Name Input Modal */}
      <AnimatePresence>
        {showNameInput && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-md p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="glass-panel w-full max-w-sm max-h-[85vh] overflow-y-auto rounded-3xl p-8 text-center"
            >
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)]">
                <img src={draftAvatar} alt="头像" className="h-full w-full object-cover" />
              </div>
              <h2 className="mb-1 text-2xl font-bold text-[var(--text)]">视界探险</h2>
              <p className="mb-4 text-sm text-[var(--text-muted)]">近视防控训练游戏 · {totalGames}款训练</p>
              <p className="mb-2 text-xs text-[var(--text-muted)]">🎨 先选一个卡通头像</p>
              <div className="mb-4 grid grid-cols-4 gap-2">
                {PRESET_IMAGE_AVATARS.filter(p => unlockedAvatars.includes(p.id)).map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => setDraftAvatar(preset.src)}
                    className={`group relative overflow-hidden rounded-xl border transition-all ${
                      draftAvatar === preset.src
                        ? 'border-[var(--accent)] ring-2 ring-[var(--accent)]/30'
                        : 'border-[var(--border)] hover:border-[var(--accent)]/50'
                    }`}
                  >
                    <img
                      src={preset.src}
                      alt={preset.label}
                      className="h-14 w-14 object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder="输入你的探险者名字"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
                className="mb-4 w-full rounded-xl border border-[var(--border)] bg-[var(--surface-elevated)] px-4 py-3.5 text-center text-[var(--text)] placeholder:text-[var(--text-muted)] outline-none transition-all focus:border-[var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/20"
              />
              <button onClick={handleNameSubmit} className="btn-primary w-full">
                开始探险 🌿
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAvatarPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-md p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="glass-panel w-full max-w-sm max-h-[85vh] overflow-y-auto rounded-3xl p-6 text-center"
            >
              <h3 className="mb-2 text-lg font-bold text-[var(--text)]">头像设置</h3>
              <p className="mb-3 text-xs text-[var(--text-muted)]">选择一个卡通形象，随训练进度解锁更多</p>

              <div className="mb-4 flex justify-center">
                <div className="h-20 w-20 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)]">
                  <img src={isImageAvatar(playerAvatar) ? playerAvatar : DEFAULT_AVATAR} alt="头像" className="h-full w-full object-cover" />
                </div>
              </div>

              {/* Preset cartoon avatars */}
              <p className="mb-2 text-xs text-[var(--text-muted)]">🎨 精选卡通头像 <span className="text-[var(--accent)]">({unlockedAvatars.length}/{PRESET_IMAGE_AVATARS.length}已解锁)</span></p>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {PRESET_IMAGE_AVATARS.map((preset) => {
                  const isUnlocked = unlockedAvatars.includes(preset.id);
                  const condition = AVATAR_UNLOCK_CONDITIONS.find(c => c.id === preset.id);
                  return (
                    <button
                      key={preset.id}
                      onClick={() => isUnlocked && handlePresetImageAvatarSelect(preset.src)}
                      disabled={!isUnlocked}
                      className={`group relative overflow-hidden rounded-xl border transition-all ${
                        !isUnlocked
                          ? 'border-[var(--border)] opacity-60 cursor-not-allowed'
                          : playerAvatar === preset.src
                            ? 'border-[var(--accent)] ring-2 ring-[var(--accent)]/30'
                            : 'border-[var(--border)] hover:border-[var(--accent)]/50'
                      }`}
                    >
                      <img
                        src={preset.src}
                        alt={preset.label}
                        className={`h-16 w-16 object-cover ${!isUnlocked ? 'grayscale blur-[1px]' : ''}`}
                        loading="lazy"
                      />
                      {!isUnlocked && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
                          <span className="text-sm">🔒</span>
                          <span className="text-[8px] text-white/90 font-medium mt-0.5 px-1 text-center leading-tight">
                            {condition?.label || '未解锁'}
                          </span>
                        </div>
                      )}
                      {isUnlocked && (
                        <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-[9px] text-white py-0.5 opacity-0 group-hover:opacity-100 transition-opacity truncate px-0.5">
                          {preset.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <button onClick={closeAvatarPicker} className="btn-primary w-full">
                完成
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header and existing account / eye-protection controls */}
      <header className="cyber-header">
        <div className="cyber-top">
          <div className="cyber-identity">
            <button onClick={openAvatarPicker} className="cyber-avatar" title="切换头像" aria-label="切换头像">
              <img src={isImageAvatar(playerAvatar) ? playerAvatar : DEFAULT_AVATAR} alt="头像" />
            </button>
            <div className="min-w-0">
              <div className="cyber-name">{playerName || '探险者'}</div>
              <div className="cyber-welcome">今日训练，从这里开始</div>
            </div>
          </div>
          <div className="cyber-status">
            <b>{energy}</b>
            <span>能量 · 签到 {checkinStreak} 天</span>
            <div className="cyber-tools">
              <button onClick={() => setShowEyePanel(!showEyePanel)} className="cyber-icon" title="护眼模式" aria-label="护眼模式" aria-expanded={showEyePanel}><Eye size={13} /></button>
              <button onClick={toggleDarkMode} className="cyber-icon" title={darkMode ? '切换亮色模式' : '切换暗黑模式'} aria-label={darkMode ? '切换亮色模式' : '切换暗黑模式'}>{darkMode ? <Sun size={13} /> : <Moon size={13} />}</button>
            </div>
          </div>
        </div>
        <div className="cyber-eye-panel">
          {/* Eye Protection Panel */}
          <AnimatePresence>
            {showEyePanel && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-3 bg-amber-50/80 dark:bg-amber-900/20 rounded-xl p-3.5 border border-amber-100 dark:border-amber-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">护眼模式</span>
                    </div>
                    <button
                      onClick={toggleEyeProtection}
                      className={`relative w-10 h-5.5 rounded-full transition-colors ${
                        eyeProtection ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow-sm transition-transform ${
                        eyeProtection ? 'translate-x-5' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  {eyeProtection && (
                    <div>
                      <div className="flex items-center justify-between text-[10px] text-amber-600/70 dark:text-amber-400/70 mb-1.5">
                        <span>冷色</span>
                        <span>色温: {colorTemperature}%</span>
                        <span>暖色</span>
                      </div>
                      <input
                        type="range"
                        min="10"
                        max="80"
                        value={colorTemperature}
                        onChange={(e) => setColorTemperature(Number(e.target.value))}
                        className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-amber-500"
                        style={{
                          background: `linear-gradient(to right, #93c5fd ${0}%, #f59e0b ${100}%)`,
                        }}
                      />
                      <p className="text-[10px] text-amber-600/60 dark:text-amber-400/60 mt-2 text-center">
                        降低蓝光，保护视力，建议夜间使用
                      </p>
                    </div>
                  )}

                  {/* Eye Rest Reminder Settings */}
                  <div className="mt-3 pt-3 border-t border-amber-200/50 dark:border-amber-700/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">⏰</span>
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-300">定时护眼提醒</span>
                      </div>
                      <button
                        onClick={toggleEyeReminder}
                        className={`relative w-10 h-5.5 rounded-full transition-colors ${
                          eyeReminderEnabled ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow-sm transition-transform ${
                          eyeReminderEnabled ? 'translate-x-5' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                    {eyeReminderEnabled && (
                      <div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {[15, 20, 30, 40].map((mins) => (
                            <button
                              key={mins}
                              onClick={() => setEyeReminderInterval(mins)}
                              className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${
                                eyeReminderInterval === mins
                                  ? 'bg-amber-500 text-white shadow-sm'
                                  : 'bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/60'
                              }`}
                            >
                              {mins}分钟
                            </button>
                          ))}
                        </div>
                        <p className="text-[10px] text-amber-600/60 dark:text-amber-400/60 mt-2">
                          每{eyeReminderInterval}分钟提醒休息，遵循20-20-20护眼法则
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Auto Night Eye Protection */}
                  <div className="mt-3 pt-3 border-t border-amber-200/50 dark:border-amber-700/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Sunset className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-300">自动夜间护眼</span>
                      </div>
                      <button
                        onClick={toggleAutoNightEyeProtection}
                        className={`relative w-10 h-5.5 rounded-full transition-colors ${
                          autoNightEyeProtection ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow-sm transition-transform ${
                          autoNightEyeProtection ? 'translate-x-5' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                    {autoNightEyeProtection && (
                      <div>
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="flex-1">
                            <label className="text-[10px] text-amber-600/70 dark:text-amber-400/70 block mb-0.5">开始时间</label>
                            <select
                              value={nightStartHour}
                              onChange={(e) => setNightStartHour(Number(e.target.value))}
                              className="w-full px-2 py-1 rounded-lg text-[11px] bg-white dark:bg-gray-700 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 outline-none"
                            >
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                              ))}
                            </select>
                          </div>
                          <span className="text-amber-500 mt-3.5">→</span>
                          <div className="flex-1">
                            <label className="text-[10px] text-amber-600/70 dark:text-amber-400/70 block mb-0.5">结束时间</label>
                            <select
                              value={nightEndHour}
                              onChange={(e) => setNightEndHour(Number(e.target.value))}
                              className="w-full px-2 py-1 rounded-lg text-[11px] bg-white dark:bg-gray-700 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 outline-none"
                            >
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <p className="text-[10px] text-amber-600/60 dark:text-amber-400/60">
                          每晚 {String(nightStartHour).padStart(2, '0')}:00 自动开启，次日 {String(nightEndHour).padStart(2, '0')}:00 关闭
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Eye Protection Usage Stats */}
                  <div className="mt-3 pt-3 border-t border-amber-200/50 dark:border-amber-700/30">
                    <div className="flex items-center gap-2 mb-2.5">
                      <Clock className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-300">使用时长统计</span>
                      {eyeProtection && liveElapsed > 0 && (
                        <span className="ml-auto text-[10px] bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded-full animate-pulse">
                          ● 使用中
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-amber-600/70 dark:text-amber-400/70">今日使用</p>
                        <p className="text-sm font-bold text-amber-700 dark:text-amber-300">{formatDuration(eyeProtectionTodaySeconds + liveElapsed)}</p>
                      </div>
                      <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-amber-600/70 dark:text-amber-400/70">累计使用</p>
                        <p className="text-sm font-bold text-amber-700 dark:text-amber-300">{formatDuration(eyeProtectionTotalSeconds + liveElapsed)}</p>
                      </div>
                    </div>
                    {/* Weekly mini bar chart */}
                    {eyeProtectionWeekly.length > 0 && (
                      <div>
                        <p className="text-[10px] text-amber-600/60 dark:text-amber-400/60 mb-1.5">近7日使用趋势</p>
                        <div className="flex items-end gap-1 h-10">
                          {getLast7Days().map((day) => {
                            const record = eyeProtectionWeekly.find(r => r.date === day.date);
                            const seconds = record?.seconds || 0;
                            const maxSeconds = Math.max(...eyeProtectionWeekly.map(r => r.seconds), 1);
                            const heightPct = Math.max(seconds > 0 ? 15 : 4, (seconds / maxSeconds) * 100);
                            return (
                              <div key={day.date} className="flex-1 flex flex-col items-center gap-0.5">
                                <div
                                  className={`w-full rounded-sm transition-all ${seconds > 0 ? 'bg-amber-400 dark:bg-amber-500' : 'bg-amber-200/50 dark:bg-amber-800/30'}`}
                                  style={{ height: `${heightPct}%` }}
                                  title={`${day.label}: ${formatDuration(seconds)}`}
                                />
                                <span className="text-[8px] text-amber-500/60 dark:text-amber-400/50">{day.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* Export Report Button */}
                    <button
                      onClick={exportEyeReport}
                      className="mt-2.5 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-300 text-[11px] font-medium hover:bg-amber-200 dark:hover:bg-amber-800/60 transition-colors active:scale-95"
                    >
                      <Download className="w-3.5 h-3.5" />
                      导出护眼报告
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </header>
      <HomeDashboard
        energy={energy}
        totalEnergy={totalEnergy}
        totalGames={totalGames}
        badges={badges}
        completedLevels={completedLevels}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        onGameClick={handleGameClick}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filteredGames={filteredGames}
      />
    </div>
  );
}
